import './AISummary.scss';
import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import { Button } from 'antd';
import { useAISummaryLogic } from './AISummary';

const AISummary = forwardRef(function AISummary({ startAndEndUnixDatesParams, onLoadingChange, isInternal = false }, ref) {
    const {
        summary,
        summaryCollapsed,
        triggerFetch,
        triggerPreload,
        toggleSummaryCollapsed,
    } = useAISummaryLogic({ startAndEndUnixDatesParams, onLoadingChange, isInternal });

    useImperativeHandle(ref, () => ({ triggerFetch }), [triggerFetch]);

    useEffect(() => {
        if (!isInternal) triggerPreload();
    }, []);

    if (!summary) return null;

    return (
        <div className='ai-summary-card'>
            <div className='ai-summary-card-header'>
                <span className='ai-summary-card-title'>Summary</span>
                <Button type='text' size='small' onClick={toggleSummaryCollapsed}>
                    {summaryCollapsed ? 'Expand ▼' : 'Collapse ▲'}
                </Button>
            </div>

            {!summaryCollapsed && (
                <div className='ai-summary-card-body'>
                    {summary.executive_overview && (
                        <div className='ai-summary-section'>
                            <p className='ai-executive-overview'>{summary.executive_overview}</p>
                        </div>
                    )}

                    {summary.top_violations?.length > 0 && (
                        <div className='ai-summary-section'>
                            <h4 className='ai-section-title'>Top Violations</h4>
                            <div className='ai-violations-grid'>
                                {summary.top_violations.map((v, i) => (
                                    <div key={i} className={`ai-violation-card trend-${v.trend}`}>
                                        <span className='violation-type'>{v.violation_type}</span>
                                        <span className='violation-count'>{v.count.toLocaleString()}</span>
                                        <span
                                            className='violation-percentage'
                                            title={`${v.percentage}% of all events captured were ${v.violation_type} violations`}
                                        >
                                            {v.percentage}%
                                        </span>
                                        <span
                                            className='violation-trend-icon'
                                            title={`The trend is ${v.trend} for ${v.violation_type} violations`}
                                        >
                                            {v.trend === 'increasing' ? '↑' : v.trend === 'decreasing' ? '↓' : '→'}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className='ai-locations-row'>
                        {summary.locations_to_watch?.length > 0 && (
                            <div className='ai-summary-section ai-locations-section'>
                                <h4 className='ai-section-title'>⚠ Locations to Watch</h4>
                                {summary.locations_to_watch.map((loc, i) => (
                                    <div key={i} className='ai-location-card watch'>
                                        <strong>{loc.site_name}</strong>
                                        <p>{loc.context}</p>
                                    </div>
                                ))}
                            </div>
                        )}

                        {summary.improved_locations?.length > 0 && (
                            <div className='ai-summary-section ai-locations-section'>
                                <h4 className='ai-section-title'>✓ Improved Locations</h4>
                                {summary.improved_locations.map((loc, i) => (
                                    <div key={i} className='ai-location-card improved'>
                                        <strong>{loc.site_name}</strong>
                                        <p>{loc.context}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {summary.user_usage_summary && (
                        <div className='ai-summary-section'>
                            <p className='ai-usage-summary'>{summary.user_usage_summary}</p>
                        </div>
                    )}

                    {summary.data_quality_notes && (
                        <div className='ai-summary-section'>
                            <h4 className='ai-section-title'>Data Quality Notes</h4>
                            <p>{summary.data_quality_notes}</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
});

export default AISummary;
